import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Target, Award, Globe, X } from 'lucide-react';
import CompanyTimeline from '../../components/CompanyTimeline';
import CaseStudies from '../../components/CaseStudies';
import SEO from '../../components/SEO';

const AboutContainer = styled.div`
  padding: 0;
  background: linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%);
  min-height: 100vh;
`;

const Hero = styled.section`
  text-align: center;
  padding: 80px 20px;
  background: linear-gradient(135deg, #ffffff 0%, #fb923c 50%, #f97316 100%);
  color: #333;
  margin: 0 -20px 60px;
  border-radius: 0 0 20px 20px;
  
  @media (max-width: 768px) {
    padding: 60px 15px;
    margin: 0 -15px 40px;
  }
  
  @media (max-width: 480px) {
    padding: 40px 10px;
    margin: 0 -10px 30px;
  }
`;

const Section = styled.section`
  margin-bottom: 80px;
  padding: 0 20px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    margin-bottom: 60px;
    padding: 0 15px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 40px;
    padding: 0 10px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  margin-top: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-top: 30px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 15px;
    margin-top: 20px;
  }
`;

const Card = styled(motion.div)`
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  text-align: center;
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: #ced6dfff;
  border-radius: 20px;
  padding: 40px;
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 768px) {
    padding: 30px 20px;
    max-width: 90vw;
  }
  
  @media (max-width: 480px) {
    padding: 20px 15px;
    max-width: 95vw;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #fb923c;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #fb923c;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #fb923c;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 15px;
  background: linear-gradient(135deg, #fb923c 0%, #f97316 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const GetStartedButton = styled.button`
  background: linear-gradient(135deg, #fb923c 0%, #f97316 100%);
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #fb923c 0%, #f97316 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: white;
`;

const About = () => {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    message: ''
  });

  const teamMembers = [
    {
      name: "Arjun Sharma",
      role: "Founder & Lead Developer",
      experience: "5+ years",
      education: "B.Tech Computer Science, IIT Delhi",
      expertise: "Full-Stack Development, UI/UX Design, MERN Stack"
    },
    {
      name: "Satyam Kumar",
      role: "Founder & Lead Civil Engineer",
      experience: "5+ years",
      education: "B.Tech Civil Engineering",
      expertise: "Architectural Design, Structural Engineering, Civil Planning"
    },
    {
      name: "Rohit Gupta",
      role: "Backend Engineer",
      experience: "3+ years",
      education: "B.Tech Software Engineering, NIT Surat",
      expertise: "Node.js, MongoDB, API Development, Cloud Infrastructure"
    },
    {
      name: "Kavya Singh",
      role: "Community Manager",
      experience: "2+ years",
      education: "B.A. Digital Marketing, Delhi University",
      expertise: "Community Building, Content Strategy, User Engagement"
    }
  ];

  const companyStats = [
    { number: "25+", label: "Projects Delivered" },
    { number: "20+", label: "Happy Clients" },
    { number: "10+", label: "Team Members" },
    { number: "8+", label: "Regions Served" }
  ];

  const certifications = [
    "SOC 2 Type II Compliant",
    "GDPR Compliant Platform",
    "MongoDB Atlas Partner",
    "React Certified Development",
    "JWT Security Standards",
    "Cloudinary Media Partner"
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const whatsappMessage = `Hi InitX Technologies!

I'm interested in your services. Here are my details:

*Name:* ${formData.name}
*Email:* ${formData.email}
*Phone:* ${formData.phone}
*Company:* ${formData.company}
*Service Needed:* ${formData.service}
*Budget Range:* ${formData.budget}
*Message:* ${formData.message}

Please get in touch with me to discuss further.

Thanks!`;
    
    const whatsappURL = `https://wa.me/918986283243?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappURL, '_blank');
    
    setShowModal(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      service: '',
      budget: '',
      message: ''
    });
  };
  const values = [
    { icon: <Target size={24} />, title: "Innovation", desc: "Cutting-edge technology solutions for modern business challenges" },
    { icon: <Users size={24} />, title: "Collaboration", desc: "Working closely with clients to deliver exceptional results" },
    { icon: <Award size={24} />, title: "Excellence", desc: "Delivering high-quality technology solutions that exceed expectations" },
    { icon: <Globe size={24} />, title: "Global Reach", desc: "Serving businesses worldwide with scalable technology solutions" }
  ];

  return (
    <AboutContainer>
      <SEO
        title="About InitX Technologies | Team & Company"
        description="Learn about InitX Technologies, our mission, values, and expert team delivering AI, cloud, and custom software solutions."
        keywords="about InitX Technologies, company, team, mission, vision, software development, IT consulting"
        type="website"
        siteName="InitX Technologies"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'About', url: '/about' }
        ]}
      />
      <Hero>
        <motion.h1 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', marginBottom: '20px' }}
        >
          About InitX Technologies
        </motion.h1>
        <motion.p 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{ fontSize: '1.2rem', opacity: 0.9 }}
        >
          Initiating Innovation with Technologies - Empowering businesses with cutting-edge technology solutions since 2021
        </motion.p>
      </Hero>

      <Section>
        <h2 style={{ fontSize: '2.5rem', textAlign: 'center', marginBottom: '30px', color: '#ffffff', fontWeight: 'bold' }}>Our Story</h2>
        <div style={{ maxWidth: '800px', margin: '0 auto', fontSize: '1.1rem', lineHeight: '1.8', color: '#ffffff' }}>
          <p style={{ marginBottom: '20px' }}>
            Founded in 2021, InitX Technologies began as a vision to bridge the gap between businesses and cutting-edge technology solutions. Headquartered in Sasaram, Rohtas, Bihar, we have grown from a startup to an emerging technology solutions provider serving growing businesses.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', justifyContent: 'center' }}>
            <img
              src="/images/msme-official.png"
              alt="MSME Certificate"
              style={{
                width: '120px',
                height: '70px',
                border: '1px solid #B8860B',
                borderRadius: '4px'
              }}
            />
            <span style={{ color: '#87ceeb', fontWeight: '600', fontSize: '14px' }}>MSME Registered Enterprise</span>
          </div>
          <p style={{ marginBottom: '20px' }}>
            Our journey started with three engineers who recognized the need for scalable, secure, and innovative technology solutions in the rapidly evolving digital landscape. Today, we're proud to serve over 25+ clients across diverse regions, delivering web development, mobile applications, digital transformation, IT consulting, and architectural designing services.
          </p>
          <p>
            With a focus on quality and innovation, InitX Technologies is establishing itself as a trusted technology partner for businesses seeking digital excellence.
          </p>
        </div>
      </Section>

      <Section>
        <h2 style={{ fontSize: '2.5rem', textAlign: 'center', marginBottom: '50px', color: '#ffffff', fontWeight: 'bold' }}>Company Statistics</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '30px', marginBottom: '60px' }}>
          {companyStats.map((stat, index) => (
            <div key={index} style={{ textAlign: 'center', padding: '30px', background: 'rgba(135,206,235,0.1)', borderRadius: '15px', border: '1px solid rgba(135,206,235,0.3)' }}>
              <h3 style={{ fontSize: '3rem', color: '#87ceeb', marginBottom: '10px', fontWeight: 'bold' }}>{stat.number}</h3>
              <p style={{ color: '#ffffff', fontSize: '1.1rem' }}>{stat.label}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* <Section>
        <h2 style={{ fontSize: '2.5rem', textAlign: 'center', marginBottom: '50px', color: '#ffffff', fontWeight: 'bold' }}>Leadership Team</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px', marginBottom: '60px' }}>
          {teamMembers.map((member, index) => (
            <div key={index} style={{ background: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
              <div style={{ width: '80px', height: '80px', background: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: 'white', fontSize: '2rem', fontWeight: 'bold' }}>
                {member.name.split(' ').map(n => n[0]).join('')}
              </div>
              <h3 style={{ color: '#333', marginBottom: '5px', fontSize: '1.3rem' }}>{member.name}</h3>
              <p style={{ color: '#fb923c', fontWeight: '600', marginBottom: '10px' }}>{member.role}</p>
              <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '8px' }}><strong>Experience:</strong> {member.experience}</p>
              <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '8px' }}><strong>Education:</strong> {member.education}</p>
              <p style={{ color: '#666', fontSize: '0.9rem' }}><strong>Expertise:</strong> {member.expertise}</p>
            </div>
          ))}
        </div>
      </Section> */}

      <Section>
        <h2 style={{ fontSize: '2.5rem', textAlign: 'center', marginBottom: '50px', color: '#ffffff', fontWeight: 'bold' }}>Certifications & Partnerships</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '60px' }}>
          {certifications.map((cert, index) => (
            <div key={index} style={{ background: 'rgba(135,206,235,0.1)', padding: '20px', borderRadius: '10px', border: '1px solid rgba(135,206,235,0.3)', textAlign: 'center' }}>
              <div style={{ color: '#87ceeb', marginBottom: '10px' }}>
                <Award size={24} />
              </div>
              <p style={{ color: '#ffffff', fontWeight: '600' }}>{cert}</p>
            </div>
          ))}
        </div>
      </Section>

      <CompanyTimeline />

      <CaseStudies />

      <Section>
        <h2 style={{ fontSize: '2.5rem', textAlign: 'center', marginBottom: '30px', color: '#ffffff', fontWeight: 'bold' }}>Our Values</h2>
        <Grid>
          {values.map((value, index) => (
            <Card
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <IconWrapper>{value.icon}</IconWrapper>
              <h3 style={{ marginBottom: '15px', color: '#333' }}>{value.title}</h3>
              <p style={{ color: '#666' }}>{value.desc}</p>
            </Card>
          ))}
        </Grid>
      </Section>

      <Section style={{ background: '#f8fafc', padding: '60px 40px', borderRadius: '20px', textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', marginBottom: '20px', color: '#333' }}>Join Our Mission</h2>
        <p style={{ fontSize: '1.2rem', color: '#666', marginBottom: '30px' }}>
          Partner with InitX Technologies for your next technology project. Whether you need AI development, cloud solutions, web applications, or digital transformation, InitX Technologies is your trusted technology partner.
        </p>
        <GetStartedButton onClick={() => window.location.href = '/contact'}>Get Started Today</GetStartedButton>
      </Section>

      <AnimatePresence>
        {showModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <ModalContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseButton onClick={() => setShowModal(false)}>
                <X size={24} />
              </CloseButton>
              
              <h2 style={{ marginBottom: '20px', color: '#333', textAlign: 'center' }}>Get Started with InitX</h2>
              <p style={{ marginBottom: '30px', color: '#666', textAlign: 'center' }}>Tell us about your project and we'll get back to you soon!</p>
              
              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label>Full Name *</Label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your full name"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your email"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your phone number"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Company Name</Label>
                  <Input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Enter your company name"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Service Needed *</Label>
                  <Select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a service</option>
                    <option value="Web Development">Web Development</option>
                    <option value="Mobile App Development">Mobile App Development</option>
                    <option value="AI & Machine Learning">AI & Machine Learning</option>
                    <option value="Cloud Solutions">Cloud Solutions</option>
                    <option value="Digital Transformation">Digital Transformation</option>
                    <option value="Other">Other</option>
                  </Select>
                </FormGroup>
                
                <FormGroup>
                  <Label>Budget Range</Label>
                  <Select
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                  >
                    <option value="">Select budget range</option>
                    <option value="$5,000 - $10,000">$5,000 - $10,000</option>
                    <option value="$10,000 - $25,000">$10,000 - $25,000</option>
                    <option value="$25,000 - $50,000">$25,000 - $50,000</option>
                    <option value="$50,000+">$50,000+</option>
                  </Select>
                </FormGroup>
                
                <FormGroup>
                  <Label>Project Details</Label>
                  <TextArea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your project requirements..."
                  />
                </FormGroup>
                
                <SubmitButton type="submit">
                  Send
                </SubmitButton>
              </form>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </AboutContainer>
  );
};

export default About;
